import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import FileDrop from '../components/FileDrop';
import { previewImport, validateRows, commitImport, uploadImages } from '../recordsApi';

const TABLE_COLUMNS = [
  ['Address__c', 'Address'],
  ['Asking_Price__c', 'Price'],
  ['Beds__c', 'Beds'],
  ['Baths__c', 'Baths'],
  ['sqm__c', 'Floor Area (m²)'],
  ['BER_Rating__c', 'BER'],
  ['Eircode', 'Eircode'],
  ['Date_Listed__c', 'Listed'],
  ['Location__Latitude__s', 'Latitude'],
  ['Location__Longitude__s', 'Longitude']
];
const RENDER_LIMIT = 1000;

// Red frame = this cell has an error, yellow frame = this cell will change an existing listing
const cellClass = (key, row) => {
  if (row.error_fields.includes(key)) return 'rec-cell-error';
  if (key === 'Eircode' && row.duplicates.length > 0) return 'rec-cell-duplicate'; // dashed orange: same Eircode as another listing
  if (row.changed_fields.includes(key)) return 'rec-cell-changed';
  return '';
};

// Clickable tile: filters the table below to matching rows (click again to clear)
const Stat = ({ value, label, tone, active, onClick }) => (
  <button type="button" className={`rec-stat${tone ? ` rec-stat-${tone}` : ''}${active ? ' rec-stat-active' : ''}`} onClick={onClick} aria-pressed={active}>
    <div className="rec-stat-value">{value}</div>
    <div className="rec-stat-label">{label}</div>
  </button>
);

// Order of the columns in the corrected CSV download
const CSV_COLUMNS = ['DaftId__c', 'Address__c', 'Asking_Price__c', 'Price__c', 'Beds__c', 'Baths__c', 'sqm__c', 'BER_Rating__c', 'Broker__c', 'DaftUrl__c', 'Eircode', 'Date_Listed__c', 'Location__Latitude__s', 'Location__Longitude__s', 'Status__c'];
const EDIT_FIELDS = ['DaftId__c', ...TABLE_COLUMNS.map(([key]) => key)];
const selectable = (r) => r.status === 'new' || r.status === 'update';
const hasDuplicates = (r) => r.status !== 'error' && r.duplicates.length > 0;

const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Single-cell text editor: Enter saves, Esc cancels, Tab saves and moves to the next cell, leaving the cell saves.
function CellEditor({ initial, onCommit, onCancel, onTab }) {
  const done = useRef(false);
  const finish = (fn) => {
    if (done.current) return;
    done.current = true;
    fn();
  };
  return (
    <input
      className="rec-cell-input"
      autoFocus
      defaultValue={initial}
      onFocus={(e) => e.target.select()}
      onBlur={(e) => finish(() => onCommit(e.target.value))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finish(() => onCommit(e.target.value));
        else if (e.key === 'Escape') finish(onCancel);
        else if (e.key === 'Tab') {
          e.preventDefault();
          const value = e.target.value;
          finish(() => onTab(value, e.shiftKey ? -1 : 1));
        }
      }}
    />
  );
}

function CsvImport() {
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState(null);
  const [original, setOriginal] = useState(null); // [{ line, raw }] exactly as read from the file
  const [edits, setEdits] = useState({}); // { line: { field: text } } changes made in the table
  const [results, setResults] = useState(null); // latest server validation, one entry per row
  const [summary, setSummary] = useState(null);
  const [included, setIncluded] = useState(() => new Set()); // lines ticked for import
  const [editing, setEditing] = useState(null); // { line, field }
  const [ignoredColumns, setIgnoredColumns] = useState(() => new Set()); // columns switched off for this import
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(null); // null | 'new' | 'update' | 'unchanged' | 'error' | 'warnings'
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const statusByLine = useRef(new Map());
  const requestSeq = useRef(0);
  const headerCheckbox = useRef(null);

  const reset = () => {
    setFile(null);
    setColumns(null);
    setOriginal(null);
    setEdits({});
    setResults(null);
    setSummary(null);
    setIncluded(new Set());
    setEditing(null);
    setIgnoredColumns(new Set());
    setError(null);
    setResult(null);
    setSearch('');
    setFilter(null);
    statusByLine.current = new Map();
    requestSeq.current += 1;
  };

  // Stores a validation response. Rows that are now valid and were not before get ticked; rows that can't be imported get unticked.
  const applyResults = (data) => {
    const previous = statusByLine.current;
    const next = new Map();
    setResults(data.rows);
    setSummary(data.summary);
    setIncluded((current) => {
      const updated = new Set(current);
      data.rows.forEach((r) => {
        const key = `${r.status}|${hasDuplicates(r)}`;
        next.set(r.line, key);
        if (!selectable(r)) updated.delete(r.line);
        else if (previous.get(r.line) !== key) {
          // New rows that share an Eircode with another listing start unticked so they are not imported by accident
          if (r.status === 'new' && hasDuplicates(r)) updated.delete(r.line);
          else updated.add(r.line);
        }
      });
      return updated;
    });
    statusByLine.current = next;
  };

  const mergedRows = (base, currentEdits) =>
    base.map((o) => ({ line: o.line, values: { ...o.raw, ...(currentEdits[o.line] || {}) } }));

  const handleFile = async ([picked]) => {
    reset();
    setFile(picked);
    setLoading(true);
    try {
      const data = await previewImport(picked);
      setColumns(data.columns);
      setOriginal(data.rows.map((r) => ({ line: r.line, raw: r.raw })));
      applyResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const revalidate = async (nextEdits, nextIgnored = ignoredColumns) => {
    const seq = ++requestSeq.current;
    setValidating(true);
    try {
      const data = await validateRows(mergedRows(original, nextEdits), [...nextIgnored]);
      if (seq === requestSeq.current) applyResults(data);
    } catch (err) {
      if (seq === requestSeq.current) setError(err.message);
    } finally {
      if (seq === requestSeq.current) setValidating(false);
    }
  };

  const currentRaw = (line, field) => {
    const edited = edits[line] && edits[line][field];
    if (edited !== undefined) return edited;
    const row = original.find((o) => o.line === line);
    return (row && row.raw[field]) || '';
  };

  const commitCell = (line, field, text) => {
    const row = original.find((o) => o.line === line);
    const originalText = (row && row.raw[field]) || '';
    if (text === currentRaw(line, field)) return;
    const nextEdits = { ...edits };
    const lineEdits = { ...(nextEdits[line] || {}) };
    if (text === originalText) delete lineEdits[field];
    else lineEdits[field] = text;
    if (Object.keys(lineEdits).length === 0) delete nextEdits[line];
    else nextEdits[line] = lineEdits;
    setEdits(nextEdits);
    revalidate(nextEdits);
  };

  const handleTab = (line, field, value, direction) => {
    commitCell(line, field, value);
    const next = EDIT_FIELDS[EDIT_FIELDS.indexOf(field) + direction];
    setEditing(next ? { line, field: next } : null);
  };

  const toggleColumn = (key) => {
    const next = new Set(ignoredColumns);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setIgnoredColumns(next);
    setEditing(null);
    revalidate(edits, next);
  };

  const resetEdits = () => {
    setEdits({});
    setEditing(null);
    revalidate({});
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    try {
      const data = await commitImport(mergedRows(original, edits), [...included], [...ignoredColumns]);
      setResult(data.summary);
      setResults(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadCsv = () => {
    const merged = mergedRows(original, edits);
    const used = CSV_COLUMNS.filter((c) => merged.some((r) => r.values[c] !== undefined));
    const lines = [used.join(','), ...merged.map((r) => used.map((c) => csvCell(r.values[c])).join(','))];
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `corrected-${file.name}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const rows = useMemo(() => {
    if (!results) return [];
    const q = search.trim().toLowerCase();
    return results.filter((r) => {
      if (filter === 'warnings') {
        if (!(r.status !== 'error' && r.warnings.length > 0)) return false;
      } else if (filter === 'duplicates') {
        if (!hasDuplicates(r)) return false;
      } else if (filter && r.status !== filter) return false;
      if (!q) return true;
      return r.id.toLowerCase().includes(q) || Object.values(r.values).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [results, search, filter]);

  const shownSelectable = rows.filter(selectable);
  const shownSelected = shownSelectable.filter((r) => included.has(r.line)).length;
  useEffect(() => {
    if (headerCheckbox.current) headerCheckbox.current.indeterminate = shownSelected > 0 && shownSelected < shownSelectable.length;
  });

  const toggleShown = () => {
    const allSelected = shownSelectable.length > 0 && shownSelected === shownSelectable.length;
    setIncluded((current) => {
      const updated = new Set(current);
      shownSelectable.forEach((r) => (allSelected ? updated.delete(r.line) : updated.add(r.line)));
      return updated;
    });
  };

  const toggleRow = (line) =>
    setIncluded((current) => {
      const updated = new Set(current);
      if (updated.has(line)) updated.delete(line);
      else updated.add(line);
      return updated;
    });

  const importable = results ? results.filter((r) => selectable(r) && included.has(r.line)).length : 0;
  const editCount = Object.values(edits).reduce((n, e) => n + Object.keys(e).length, 0);
  const statusLabel = (r) => ({ new: 'New', update: `Update (${r.changed_fields.length})`, unchanged: 'No changes', error: 'Error' }[r.status]);
  const tile = (key) => () => setFilter(filter === key ? null : key);

  return (
    <section className="rec-section">
      <h2>1. Import a CSV</h2>
      <FileDrop accept={['.csv']} title="Drop a CSV file here, or choose it from your computer" hint="Rows are checked first; nothing is saved until you click Import." onFiles={handleFile} />

      {loading && <p className="rec-muted rec-sm rec-drop-note">Checking {file && file.name}…</p>}
      {error && <p className="rec-error rec-sm rec-drop-note">{error}</p>}

      {result && (
        <div className="rec-result">
          <h3>Import complete</h3>
          <p className="rec-sm">
            {result.inserted} added · {result.updated} updated · {result.unchanged} unchanged · {result.skipped_unselected} not selected · {result.skipped_errors} skipped (errors)
          </p>
          <div className="rec-result-actions">
            <Link to="/listings" className="rec-btn-primary">View listings</Link>
            <button type="button" className="rec-btn" onClick={reset}>Import another file</button>
          </div>
        </div>
      )}

      {results && summary && (
        <>
          <div className="rec-cards">
            <div className="rec-card">
              <h3>File</h3>
              <div className="rec-file">
                <div>
                  <div className="rec-file-name">{file.name}</div>
                  <div className="rec-muted rec-sm">{summary.total} rows · {columns.recognised.length} columns used</div>
                </div>
                <button type="button" className="rec-btn" onClick={reset}>Remove</button>
              </div>
              {columns.renamed && Object.keys(columns.renamed).length > 0 && (
                <p className="rec-muted rec-sm rec-card-note">
                  Mapped: {Object.entries(columns.renamed).map(([from, to]) => `${from} → ${to}`).join(', ')}
                </p>
              )}
              {columns.ignored.length > 0 && (
                <p className="rec-muted rec-sm rec-card-note">Ignored columns: {columns.ignored.join(', ')}</p>
              )}
            </div>
            <div className="rec-card">
              <h3>How this works</h3>
              <ul className="rec-help rec-sm">
                <li>Tick the rows you want to import. Rows with errors can’t be ticked until they’re fixed.</li>
                <li>Click any cell to edit it; the row is re-checked straight away. Untick a column above the table to leave it out of the import.</li>
                <li><span className="rec-key rec-key-error" /> error · <span className="rec-key rec-key-changed" /> changes an existing listing · <span className="rec-key rec-key-edited" /> edited by you · <span className="rec-key rec-key-dup" /> same Eircode as another listing</li>
              </ul>
            </div>
          </div>

          <div className="rec-stats">
            <Stat value={summary.total} label="Rows in file" active={filter === null} onClick={() => setFilter(null)} />
            <Stat value={summary.new} label="New" tone="good" active={filter === 'new'} onClick={tile('new')} />
            <Stat value={summary.update} label="To update" tone="changed" active={filter === 'update'} onClick={tile('update')} />
            <Stat value={summary.unchanged} label="Unchanged" active={filter === 'unchanged'} onClick={tile('unchanged')} />
            <Stat value={summary.duplicates} label="Possible duplicates" tone={summary.duplicates ? 'dup' : undefined} active={filter === 'duplicates'} onClick={tile('duplicates')} />
            <Stat value={summary.error} label="Errors (not imported)" tone={summary.error ? 'bad' : undefined} active={filter === 'error'} onClick={tile('error')} />
            <Stat value={summary.warnings} label="With warnings" tone={summary.warnings ? 'warn' : undefined} active={filter === 'warnings'} onClick={tile('warnings')} />
          </div>

          <div className="rec-toolbar">
            <input type="search" className="rec-search rec-toolbar-search" placeholder="Search the table" aria-label="Search the table" value={search} onChange={(e) => setSearch(e.target.value)} />
            <span className="rec-muted rec-sm rec-toolbar-count">
              {rows.length} of {summary.total} rows · {importable} selected{validating ? ' · re-checking…' : ''}
            </span>
            <button type="button" className="rec-btn" disabled={importable === 0} onClick={() => setIncluded(new Set())}>Clear selection</button>
            {editCount > 0 && <button type="button" className="rec-btn" onClick={resetEdits}>Reset {editCount} edit{editCount === 1 ? '' : 's'}</button>}
            <button type="button" className="rec-btn" onClick={downloadCsv}>Download corrected CSV</button>
            <button type="button" className="rec-btn-primary" disabled={importing || validating || importable === 0} onClick={handleImport}>
              {importing ? 'Importing…' : `Import ${importable} row${importable === 1 ? '' : 's'}`}
            </button>
          </div>

          <div className="rec-table-wrap">
            <table className="rec-table">
              <thead>
                <tr className="rec-colcheck-row">
                  <th colSpan={3} className="rec-colcheck-label">Import this column:</th>
                  <th className="rec-colcheck" title="The Daft ID identifies the listing, so it is always used">
                    <input type="checkbox" checked disabled aria-label="Import Daft ID column" />
                  </th>
                  {TABLE_COLUMNS.map(([key, label]) => (
                    <th key={key} className="rec-colcheck">
                      <input type="checkbox" checked={!ignoredColumns.has(key)} onChange={() => toggleColumn(key)} aria-label={`Import ${label} column`} />
                    </th>
                  ))}
                  <th />
                </tr>
                <tr>
                  <th className="rec-check-col">
                    <input ref={headerCheckbox} type="checkbox" aria-label="Select or clear all shown rows" title="Select or clear all shown rows"
                      checked={shownSelectable.length > 0 && shownSelected === shownSelectable.length} disabled={shownSelectable.length === 0} onChange={toggleShown} />
                  </th>
                  <th>Line</th>
                  <th>Status</th>
                  <th>Daft ID</th>
                  {TABLE_COLUMNS.map(([key, label]) => <th key={key} className={ignoredColumns.has(key) ? 'rec-col-ignored' : undefined}>{label}</th>)}
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, RENDER_LIMIT).map((r) => {
                  const canSelect = selectable(r);
                  const isIncluded = included.has(r.line);
                  const rowClass = r.status === 'error' ? 'rec-row-error' : !isIncluded ? 'rec-row-excluded' : undefined;
                  const renderCell = (key, display) => {
                    const edited = edits[r.line] && edits[r.line][key] !== undefined;
                    const isEditing = editing && editing.line === r.line && editing.field === key;
                    const hints = [];
                    if (edited) hints.push(`Original: ${original.find((o) => o.line === r.line).raw[key] || '(empty)'}`);
                    if (r.changed_fields.includes(key)) hints.push(`Current value: ${r.previous[key] == null ? '(empty)' : r.previous[key]}`);
                    const columnIgnored = ignoredColumns.has(key);
                    const classes = [key === 'Address__c' ? 'rec-cell-wrap' : '', columnIgnored ? 'rec-col-ignored' : 'rec-editable', columnIgnored ? '' : cellClass(key, r), edited ? 'rec-cell-edited' : ''].filter(Boolean).join(' ');
                    return (
                      <td key={key} className={classes} title={columnIgnored ? 'This column is not imported' : hints.join(' · ') || undefined} onClick={() => !isEditing && !columnIgnored && setEditing({ line: r.line, field: key })}>
                        {isEditing ? (
                          <CellEditor
                            initial={currentRaw(r.line, key)}
                            onCommit={(text) => { commitCell(r.line, key, text); setEditing(null); }}
                            onCancel={() => setEditing(null)}
                            onTab={(text, direction) => handleTab(r.line, key, text, direction)}
                          />
                        ) : display == null || display === '' ? <span className="rec-muted">—</span> : String(display)}
                      </td>
                    );
                  };
                  return (
                    <tr key={r.line} className={rowClass}>
                      <td className="rec-check-col">
                        <input type="checkbox" checked={isIncluded && canSelect} disabled={!canSelect}
                          title={canSelect ? undefined : r.status === 'error' ? 'Fix the errors to import this row' : 'Nothing to import for this row'}
                          aria-label={`Import line ${r.line}`} onChange={() => toggleRow(r.line)} />
                      </td>
                      <td className="rec-muted">{r.line}</td>
                      <td><span className={`rec-badge rec-badge-${r.status}`} title={r.changed_fields.join(', ')}>{statusLabel(r)}</span></td>
                      {renderCell('DaftId__c', r.id)}
                      {TABLE_COLUMNS.map(([key]) => renderCell(key, ignoredColumns.has(key) ? r.raw[key] : r.invalid[key] !== undefined ? r.invalid[key] : r.values[key]))}
                      <td className="rec-cell-wrap rec-issues">
                        {r.errors.map((m) => <div key={m} className="rec-error">{m}</div>)}
                        {r.warnings.map((m) => <div key={m} className="rec-warn">{m}</div>)}
                        {r.status !== 'error' && r.duplicates.map((d) => (
                          <div key={`${d.source}-${d.id}`} className="rec-dup">
                            Possible duplicate: same Eircode as{' '}
                            {d.source === 'db'
                              ? <a href={`/listings/${encodeURIComponent(d.id)}`} target="_blank" rel="noreferrer">{d.id}</a>
                              : <>line {d.line} ({d.id})</>}
                            {d.address ? ` – ${d.address}` : ''}{d.source === 'db' && d.asking_price ? ` (${d.asking_price})` : ''}
                          </div>
                        ))}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={TABLE_COLUMNS.length + 5} className="rec-muted rec-empty">No rows to show.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {rows.length > RENDER_LIMIT && (
            <p className="rec-muted rec-sm rec-drop-note">Showing the first {RENDER_LIMIT} of {rows.length} rows. Use search or the tiles above to narrow down. All ticked rows are imported.</p>
          )}
        </>
      )}
    </section>
  );
}

function ImageUpload() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const addFiles = (picked) => {
    setResults(null);
    setFiles((current) => {
      const names = new Set(current.map((f) => f.name));
      return [...current, ...picked.filter((f) => !names.has(f.name))];
    });
  };

  const handleUpload = async () => {
    setUploading(true);
    setError(null);
    setProgress([0, files.length]);
    try {
      setResults(await uploadImages(files, (done, total) => setProgress([done, total])));
      setFiles([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const saved = results ? results.filter((r) => r.status !== 'rejected').length : 0;
  const rejected = results ? results.length - saved : 0;
  const orphans = results ? results.filter((r) => r.status !== 'rejected' && r.listing_exists === false) : [];

  return (
    <section className="rec-section">
      <h2>2. Upload images</h2>
      <FileDrop
        accept={['.jpg', '.jpeg']}
        multiple
        title="Drop JPG files here, or choose them from your computer"
        hint="Files must be named after the Daft ID, e.g. 6507846.jpg. They are saved to the daft-images folder; an existing image with the same name is replaced."
        onFiles={addFiles}
      />
      {error && <p className="rec-error rec-sm rec-drop-note">{error}</p>}

      {files.length > 0 && (
        <div className="rec-card rec-card-spaced">
          <div className="rec-file">
            <div>
              <div className="rec-file-name">{files.length} file{files.length === 1 ? '' : 's'} ready</div>
              <div className="rec-muted rec-sm">{files.slice(0, 6).map((f) => f.name).join(', ')}{files.length > 6 ? ', …' : ''}</div>
            </div>
            <div className="rec-form-actions">
              <button type="button" className="rec-btn" disabled={uploading} onClick={() => setFiles([])}>Clear</button>
              <button type="button" className="rec-btn-primary" disabled={uploading} onClick={handleUpload}>
                {uploading && progress ? `Uploading ${progress[0]}/${progress[1]}…` : `Upload ${files.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {results && (
        <div className="rec-result">
          <h3>Upload finished</h3>
          <p className="rec-sm">{saved} saved · {rejected} rejected{orphans.length > 0 ? ` · ${orphans.length} saved for IDs with no listing yet` : ''}</p>
          {(rejected > 0 || orphans.length > 0) && (
            <ul className="rec-sm rec-result-list">
              {results.filter((r) => r.status === 'rejected').map((r) => <li key={r.name} className="rec-error">{r.name}: {r.reason}</li>)}
              {orphans.map((r) => <li key={r.name} className="rec-warn">{r.name}: saved, but no listing has Daft ID {r.id}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

export default function ImportPage() {
  return (
    <div className="rec rec-page rec-wide">
      <Link to="/listings" className="rec-btn rec-back">
        <span aria-hidden="true">←</span> Back to listings
      </Link>
      <div className="rec-header rec-header-center">
        <h1>Import</h1>
      </div>
      <CsvImport />
      <ImageUpload />
    </div>
  );
}
